<template>
  <div class="time-picker__container wd-overflow-y-auto wd-py-4 wd-flex wd-flex-col wd-gap-4 xs:wd-max-h-[50vh]">
    <div v-for="group in timeSlotGroups" :key="group.id">
      <div class="wd-flex wd-items-center wd-justify-between wd-gap-2 wd-px-5 wd-pb-2">
        <span class="wd-text-otempsm wd-text-gray80">
          {{ getGroupHeader(group.id) }}
        </span>
        <v-checkbox
          :model-value="isGroupFullySelected(group)"
          :label="t('filter_all_time')"
          density="compact"
          hide-details
          color="orange50"
          class="wd-shrink-0 time-picker__select-all"
          @update:model-value="onSelectAllToggle(group, $event)"
        />
      </div>
      <div class="wd-grid wd-grid-cols-3 wd-gap-2 wd-px-5">
        <v-btn
          v-for="slot in group.slots"
          :key="slot.time"
          class="time-slot-btn wd-text-omd"
          :class="{ 'time-slot-btn--selected': isSlotSelected(slot.time) }"
          :color="isSlotSelected(slot.time) ? 'orange50' : 'gray50'"
          variant="outlined"
          @click="onSlotClick(slot.time)"
        >
          {{ slot.time }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { type TimePeriodId, type TimePeriodGroup, useTimeFilterSelection } from '@myfunnow/web-core/time-filter'

import { TIME_PERIOD_I18N_MAP } from '@/constants/filters'

interface Props {
  value?: string[]
  selectedDate?: string | null
  isClear?: boolean
}

interface Emits {
  (e: 'return-filter', value: (string[] | false)[]): void
  (e: 'update-clear-btn', activated: boolean): void
}

const props = withDefaults(defineProps<Props>(), {
  value: () => [],
  selectedDate: null,
  isClear: false,
})

const emit = defineEmits<Emits>()

const { t } = useI18n()

const selectedTimes = ref<string[]>([...props.value])

const selectedDateRef = computed(() => props.selectedDate || null)

const { timeSlotGroups, isSlotSelected, isGroupFullySelected, handleSlotClick, handleSelectAllToggle } = useTimeFilterSelection(
  selectedDateRef,
  selectedTimes
)

watch(
  () => props.isClear,
  newIsClear => {
    if (newIsClear) {
      selectedTimes.value = []
      emitFilterChange()
    }
  }
)

function emitFilterChange() {
  const isActivated = selectedTimes.value.length > 0

  emit('update-clear-btn', isActivated)
  emit('return-filter', [selectedTimes.value])
}

function onSlotClick(time: string) {
  handleSlotClick(time)
  emitFilterChange()
}

function onSelectAllToggle(group: TimePeriodGroup, checked: boolean | null) {
  handleSelectAllToggle(group, !!checked)
  emitFilterChange()
}

function getGroupHeader(groupId: TimePeriodId): string {
  const { labelI18n, rangeTextI18n } = TIME_PERIOD_I18N_MAP[groupId]

  return `${t(labelI18n)} ${t(rangeTextI18n)}`
}
</script>

<style lang="scss" scoped>
.time-picker__select-all {
  :deep(.v-label) {
    @apply wd-text-otempsm wd-text-gray80;
  }

  :deep(.v-selection-control) {
    @apply wd-min-h-0;
    @apply wd-items-center !important;
  }

  :deep(.v-selection-control__input) {
    @apply wd-w-4 wd-h-4 !important;

    .v-icon--size-default {
      @apply wd-text-[16px];

      &:not(.mdi-checkbox-marked) {
        @apply wd-text-gray50;
      }
    }
  }
}

.time-slot-btn {
  &:not(.time-slot-btn--selected) {
    :deep(.v-btn__content) {
      @apply wd-text-black50;
    }
  }
}
</style>
